import { AdminAbility } from '@repo/sdk/backoffice-abilities'
import { type ApplicationManagerDashboard, rankingExport } from '@repo/sdk/gen'
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/card'
import { PageHeader } from '@repo/ui/components/page-header'
import { Skeleton } from '@repo/ui/skeleton'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { type TKey, useTranslation } from '@/lib/i18n'
import { requireAdminPermission } from '@/modules/permissions/lib/require-admin-permission'
import { CallStateBadge } from '../-components/call-state-badge'
import { TableExportMenu } from '../-components/table-export-menu'
import { downloadExportResult } from '../-lib/export-download'
import { managerDashboardQueryOptions } from '../-query/query-options'

type CallState =
	ApplicationManagerDashboard['residences'][number]['call']['state']

const RANKING_NOTE = {
	draft: 'admin-exports:manager.ranking_none',
	published: 'admin-exports:manager.ranking_none',
	open: 'admin-exports:manager.ranking_provisional',
	closed: 'admin-exports:manager.ranking_in_force'
} as const satisfies Record<CallState, TKey>

/** The day in Italy as yyyy-mm-dd, the one the server names the file with. */
function todayInRome() {
	return new Intl.DateTimeFormat('sv-SE', {
		timeZone: 'Europe/Rome'
	}).format(new Date())
}

export const Route = createFileRoute('/(private)/exports/')({
	beforeLoad: requireAdminPermission([AdminAbility.CallManage]),
	component: ExportsPage
})

/**
 * What a facility manager hands to the bodies that check its calls (PRD
 * 4.12.2): for each residence, its ranking as CSV or XLSX. While the call is
 * open it is the provisional one. Every file is recorded as an access to the
 * students' data.
 */
function ExportsPage() {
	const { t } = useTranslation()
	const query = useQuery(managerDashboardQueryOptions(undefined))

	return (
		<main className='flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 md:p-6'>
			<PageHeader title={t('admin-exports:manager.page_title')} />
			<p className='max-w-prose text-muted-foreground text-sm'>
				{t('admin-exports:manager.description')}
			</p>
			{query.isPending && <Skeleton className='h-40' />}
			{query.isError && (
				<p className='text-destructive text-sm'>
					{t('common:detail_section.error')}
				</p>
			)}
			{query.data && (
				<Card>
					<CardHeader>
						<CardTitle>
							{t('admin-exports:manager.ranking_title')}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<ul className='flex flex-col divide-y divide-border'>
							{query.data.residences.map((item) => (
								<RankingExportRow
									academicYear={item.call.academicYear}
									key={item.id}
									name={item.name}
									residenceId={item.id}
									slug={item.slug}
									state={item.call.state}
								/>
							))}
						</ul>
					</CardContent>
				</Card>
			)}
		</main>
	)
}

function RankingExportRow({
	residenceId,
	name,
	slug,
	academicYear,
	state
}: {
	residenceId: string
	name: string
	slug: string
	academicYear: string
	state: CallState
}) {
	const { t } = useTranslation()
	const download = useMutation({
		mutationFn: async (format: 'csv' | 'xlsx') =>
			downloadExportResult(
				await rankingExport({
					body: { format, mode: 'auto', filters: { residenceId } }
				}),
				// The same name the server gives an export queued instead.
				`graduatoria-${slug}-${academicYear.replace('/', '-')}-${todayInRome()}.${format}`
			),
		onSuccess: (result) =>
			toast.success(
				result === 'downloaded'
					? t('admin-exports:dialog.downloaded')
					: t('admin-exports:dialog.started')
			),
		onError: (error) => toast.error(formatError(error))
	})

	return (
		<li className='flex flex-wrap items-center justify-between gap-3 py-3'>
			<div className='flex flex-col gap-1'>
				<span className='font-medium'>{name}</span>
				<div className='flex flex-wrap items-center gap-2'>
					<CallStateBadge state={state} />
					<span className='text-muted-foreground text-xs'>
						{t(RANKING_NOTE[state])}
					</span>
				</div>
			</div>
			{/* Before the call opens there is no ranking, not even a provisional one. */}
			{(state === 'open' || state === 'closed') && (
				<TableExportMenu
					onSelectFormat={download.mutate}
					pending={download.isPending}
				/>
			)}
		</li>
	)
}
